'use client';

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { DownloadIcon } from 'tdesign-icons-react';
import { useLocalGlobalStore } from '@ssv-website/hooks';
import { MessagePlugin } from 'tdesign-react';
import styles from '../../design-assets.module.css';
import {
  getDesignAssetDownloadPermission,
  getDesignAssetDownloadURL,
  type DesignAssetPreview,
} from '../../api';
import {
  DesignEid,
  dtElementParams,
  reportDesignClick,
} from '../../tracking';

const markdownLinkPattern = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
const downloadFileIDKey = 'download_file_id';

export const buildDesignAssetLoginURL = (
  currentURL: string,
  fileID: string
) => {
  const returnURL = new URL(currentURL);
  returnURL.searchParams.set(downloadFileIDKey, fileID);
  return `/login?s_url=${encodeURIComponent(returnURL.toString())}`;
};

function renderDescription(description: string) {
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;
  markdownLinkPattern.lastIndex = 0;
  while ((match = markdownLinkPattern.exec(description))) {
    if (match.index > cursor)
      nodes.push(description.slice(cursor, match.index));
    nodes.push(
      <a
        dt-eid={DesignEid.AssetExtLink}
        dt-imp-ignore="true"
        dt-params={dtElementParams({
          link_url: match[2],
          link_name: match[1],
        })}
        href={match[2]}
        key={`${match.index}-${match[2]}`}
        rel="noreferrer"
        target="_blank"
      >
        {match[1]}
      </a>
    );
    cursor = match.index + match[0].length;
  }
  if (cursor < description.length) nodes.push(description.slice(cursor));
  return nodes.length ? nodes : description;
}

export function AssetSection({
  asset,
  first,
  groupID,
  pgid,
}: {
  asset: DesignAssetPreview;
  first: boolean;
  groupID: string;
  pgid?: string;
}) {
  const isLogin = useLocalGlobalStore((state) => state.is_login);
  const [downloadingFileID, setDownloadingFileID] = useState<string | null>(
    null
  );

  const downloadWithFreshPermission = useCallback(
    async (
      fileID: string,
      fallbackFileName: string,
      isActive: () => boolean = () => true
    ) => {
      try {
        const result = await getDesignAssetDownloadPermission(
          groupID,
          asset.asset_id,
          fileID
        );
        if (!isActive()) return;
        if (!result.canDownload) {
          MessagePlugin.error('当前用户暂无下载权限');
          return;
        }
        const fileName = result.file?.file_name || fallbackFileName;
        window.location.assign(getDesignAssetDownloadURL(fileID, fileName));
      } catch {
        if (isActive()) MessagePlugin.error('品牌资源服务暂不可用');
      } finally {
        if (isActive()) setDownloadingFileID(null);
      }
    },
    [asset.asset_id, groupID]
  );

  useEffect(() => {
    if (!isLogin) return;

    const searchParams = new URLSearchParams(window.location.search);
    const fileID = searchParams.get(downloadFileIDKey);
    const file = asset.downloads?.find((item) => item.file_id === fileID);
    if (!file) return;

    searchParams.delete(downloadFileIDKey);
    const query = searchParams.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}`
    );
    let isActive = true;
    setDownloadingFileID(fileID);
    void downloadWithFreshPermission(
      file.file_id,
      file.file_name,
      () => isActive
    );
    return () => {
      isActive = false;
    };
  }, [asset.asset_id, asset.downloads, downloadWithFreshPermission, isLogin]);

  const handleDownload = (
    event: React.MouseEvent<HTMLAnchorElement>,
    fileID: string,
    fileName: string
  ) => {
    event.preventDefault();
    if (!isLogin) {
      reportDesignClick(pgid, DesignEid.AssetDownloadBtn, {
        asset_id: asset.asset_id,
        asset_name: asset.asset_name,
        file_id: fileID,
        file_name: fileName,
      });
      window.location.href = buildDesignAssetLoginURL(
        window.location.href,
        fileID
      );
      return;
    }

    if (downloadingFileID) return;
    setDownloadingFileID(fileID);
    void downloadWithFreshPermission(fileID, fileName);
  };

  return (
    <section
      className={styles.assetSection}
      id={`asset-${asset.asset_id}`}
      data-first={first}
    >
      <h1>{asset.asset_name}</h1>
      <p className={styles.assetDescription}>
        {renderDescription(asset.description)}
      </p>

      {asset.downloads?.length ? (
        <div className={styles.downloadRow}>
          {asset.downloads.map((file) => (
            <a
              className={styles.downloadButton}
              download={file.file_name}
              dt-eid={DesignEid.AssetDownloadBtn}
              dt-imp-ignore="true"
              dt-params={dtElementParams({
                asset_id: asset.asset_id,
                asset_name: asset.asset_name,
                file_id: file.file_id,
                file_name: file.file_name,
              })}
              href={getDesignAssetDownloadURL(file.file_id, file.file_name)}
              key={file.file_id}
              onClick={(event) =>
                handleDownload(event, file.file_id, file.file_name)
              }
            >
              <DownloadIcon aria-hidden="true" size="16" />
              {file.file_name}
            </a>
          ))}
        </div>
      ) : null}

      {asset.images?.length ? (
        <div className={styles.figureList}>
          {asset.images.map((image) => (
            <AssetImage image={image} key={image.file_id} />
          ))}
        </div>
      ) : null}

      {asset.video?.url ? (
        <figure className={styles.figureItem}>
          <video
            controls
            controlsList="nodownload noremoteplayback"
            disablePictureInPicture
            disableRemotePlayback
            onContextMenu={(event) => event.preventDefault()}
            playsInline
            preload="metadata"
            src={asset.video.url}
          />
        </figure>
      ) : null}
      {asset.video?.file_description || asset.video?.file_link_url ? (
        <p className={styles.imageDescription}>
          {asset.video.file_link_url ? (
            <a
              dt-eid={DesignEid.AssetExtLink}
              dt-imp-ignore="true"
              dt-params={dtElementParams({
                link_url: asset.video.file_link_url,
                link_name:
                  asset.video.file_description || asset.video.file_link_url,
              })}
              href={asset.video.file_link_url}
              rel="noreferrer"
              target="_blank"
            >
              {asset.video.file_description || asset.video.file_link_url}
            </a>
          ) : (
            asset.video.file_description
          )}
        </p>
      ) : null}
    </section>
  );
}

function AssetImage({
  image,
}: {
  image: NonNullable<DesignAssetPreview['images']>[number];
}) {
  const [attempt, setAttempt] = useState(0);
  const failed = attempt >= 2;
  return (
    <figure
      className={styles.figureItem}
      data-size={image.display_size || 'md'}
    >
      {failed ? (
        <div className={styles.imageFallback}>图片加载失败</div>
      ) : (
        <img
          alt={image.alt || ''}
          draggable={false}
          onContextMenu={(event) => event.preventDefault()}
          onDragStart={(event) => event.preventDefault()}
          src={
            attempt > 0
              ? `${image.url}${
                  image.url.includes('?') ? '&' : '?'
                }_retry=${attempt}`
              : image.url
          }
          onError={() => setAttempt((value) => value + 1)}
        />
      )}
      {image.file_description || image.file_link_url ? (
        <figcaption className={styles.imageDescription}>
          {image.file_link_url ? (
            <a
              dt-eid={DesignEid.AssetExtLink}
              dt-imp-ignore="true"
              dt-params={dtElementParams({
                link_url: image.file_link_url,
                link_name: image.file_description || image.file_link_url,
              })}
              href={image.file_link_url}
              rel="noreferrer"
              target="_blank"
            >
              {image.file_description || image.file_link_url}
            </a>
          ) : (
            image.file_description
          )}
        </figcaption>
      ) : null}
    </figure>
  );
}
